import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { RecipeElementType } from "../RecipeCard/RecipeCard";
import { useRouter } from "next/router";
import { getFiltersFromBrowser } from "@/modules/RecipeGrid/queryParamsHelper";
import { RecipeGridResponseType } from "@/modules/RecipeGrid/fetchRecipes";

export type TipTopfSortType =
  | "sort_aktuell"
  | "sort_bewertung"
  | "sort_az"
  | "sort_za";

export interface FetchItemsProps {
  page: number;
  sortType: TipTopfSortType;
  filters: RecipeFiltersType;
  produkttyp: number;
  postsPerPage?: number;
}

export type RecipeFiltersType = {
  categories: number[];
  difficultyLevel: number | null;
};

export const isTipTopfSortType = (value: unknown): value is TipTopfSortType => {
  return (
    typeof value === "string" &&
    ["sort_aktuell", "sort_az", "sort_za", "sort_bewertung"].includes(value)
  );
};

interface GridProps {
  children: (
    items: RecipeElementType[],
    sortType: TipTopfSortType,
    loading: boolean,
  ) => ReactNode;
  postsPerPage: number;
  fetchItems: (
    props: FetchItemsProps,
  ) =>
    | Promise<{ posts: RecipeElementType[]; totalPosts: number }>
    | { posts: RecipeElementType[]; totalPosts: number };
  initialData: RecipeGridResponseType | null;
  sortOptions?: string[];
  labels?: { loading?: string; noItems?: string };
  initialFilters?: RecipeFiltersType;
}

type GridContextProps = {
  loading: boolean;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  postsPerPage: number;
  postsTotal: number;
  items: RecipeElementType[];
  sortType: TipTopfSortType;
  setSortType: (sortType: TipTopfSortType) => void;
  setItems: Dispatch<SetStateAction<RecipeElementType[]>>;
  setTotalPosts: (totalPosts: number) => void;
  sortOptions?: string[];
  labels: { loading: string; noItems: string };
  handleLoadMore: () => void;
  filters: RecipeFiltersType;
  allKleinerTiptopfRecipesLoaded: boolean;
  produkttyp: number; // 1 for "Tiptopf", 2 for "Der kleine Tiptopf"
  totalPostsTiptopf: number;
  canLoadMore: boolean;
};

const GridContext = createContext<GridContextProps | null>(null);

export function useGrid() {
  const context = useContext(GridContext);
  if (!context) throw new Error("useGrid must be used within a <Grid />");
  return context;
}

const Grid = ({
  children,
  postsPerPage,
  fetchItems,
  sortOptions,
  initialData,
  labels: labelsProps,
  initialFilters,
}: GridProps) => {
  const router = useRouter();
  const { getLabel } = useGlobalContext();

  // Filter direkt aus der Query ableiten
  const filters = initialFilters || getFiltersFromBrowser(router.query);

  // --- State ---
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<RecipeElementType[]>(
    initialData?.posts ?? [],
  );

  const [totalPosts, setTotalPosts] = useState(initialData?.totalPosts ?? -1);
  const [totalPostsTiptopf, setTotalPostsTiptopf] = useState(-1);

  const [currentPage, setCurrentPage] = useState(1);
  const [currentTiptopfPage, setCurrentTipTopfPage] = useState(1);
  const [produkttyp, setProdukttyp] = useState(2);

  const [sortType, setSortType] = useState<TipTopfSortType>("sort_aktuell");
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [allKleinerTiptopfRecipesLoaded, setAllKleinerTiptopfRecipesLoaded] =
    useState(false);

  // Ref, um den allerersten Render-Zyklus zu kontrollieren
  const isFirstRender = useRef(true);

  const labels = {
    loading: labelsProps?.loading ?? getLabel("loading", "loading"),
    noItems: labelsProps?.noItems ?? getLabel("no_items", "no items"),
  };

  const fetchData = async ({
    refetch,
    page,
    recipeType,
    customPostsPerPage,
  }: {
    refetch: boolean;
    page: number;
    recipeType: number;
    customPostsPerPage?: number;
  }) => {
    setLoading(true);
    try {
      const { posts, totalPosts: fetchedTotal } = await fetchItems({
        page,
        sortType,
        filters, // nutzt die aktuellen Filter aus der Query
        produkttyp: recipeType,
        postsPerPage: customPostsPerPage || postsPerPage,
      });

      if (recipeType === 2) {
        setTotalPosts(fetchedTotal);
      } else {
        setTotalPostsTiptopf(fetchedTotal);
      }

      setItems((prev) => (refetch ? posts : [...prev, ...posts]));
      setProdukttyp(recipeType);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (loading) return;
    const hasDifficultyFilter = filters.difficultyLevel !== null;

    if (produkttyp === 2) {
      if (items.length < totalPosts) {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        fetchData({ page: nextPage, refetch: false, recipeType: 2 });
      } else if (!hasDifficultyFilter) {
        setAllKleinerTiptopfRecipesLoaded(true);
        fetchData({ page: 1, refetch: false, recipeType: 1 });
        setCurrentTipTopfPage(1);
      } else {
        setCanLoadMore(false);
      }
    } else {
      if (items.length < totalPosts + totalPostsTiptopf) {
        const nextTiptopfPage = currentTiptopfPage + 1;
        setCurrentTipTopfPage(nextTiptopfPage);
        fetchData({ page: nextTiptopfPage, refetch: false, recipeType: 1 });
      } else {
        setCanLoadMore(false);
      }
    }
  };

  useEffect(() => {
    if (!router.isReady) return;
    // Prüfen, ob wir "echte" Filter in der URL haben (außer page)
    const hasActiveFilters =
      filters.categories.length > 0 ||
      filters.difficultyLevel !== null ||
      (router.query.sort && router.query.sort !== "sort_aktuell");

    // FALL 1: Erster Render + SSR Daten + Keine Filter
    if (
      isFirstRender.current &&
      initialData?.posts?.length &&
      initialData.posts.length > 0 &&
      !hasActiveFilters
    ) {
      isFirstRender.current = false;
      return;
    }

    // FALL 2: Filter vorhanden ODER kein erster Render mehr
    isFirstRender.current = false;

    const urlPage = router.query.page
      ? parseInt(router.query.page as string)
      : 1;

    // UI zurücksetzen für neuen Fetch
    setItems([]);
    setProdukttyp(2);
    setAllKleinerTiptopfRecipesLoaded(false);
    setCurrentPage(urlPage);
    setCanLoadMore(true);

    fetchData({
      refetch: true,
      page: 1,
      recipeType: 2,
      customPostsPerPage: urlPage * postsPerPage,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, JSON.stringify(filters), sortType]);

  // Load-More Button Sichtbarkeit
  useEffect(() => {
    const isTyp2Done = totalPosts !== -1 && items.length >= totalPosts;
    const isTyp1Done =
      totalPostsTiptopf !== -1 &&
      items.length >= totalPosts + totalPostsTiptopf;
    const hasDifficultyFilter = filters.difficultyLevel !== null;

    if (produkttyp === 2) {
      setCanLoadMore(!(isTyp2Done && hasDifficultyFilter));
    } else {
      setCanLoadMore(!isTyp1Done);
    }
  }, [
    items.length,
    totalPosts,
    totalPostsTiptopf,
    produkttyp,
    filters.difficultyLevel,
  ]);

  return (
    <div className="grid-ref">
      <GridContext.Provider
        value={{
          loading,
          currentPage,
          setCurrentPage,
          postsPerPage,
          postsTotal: totalPosts,
          items,
          sortType,
          setSortType,
          sortOptions,
          labels,
          setItems,
          setTotalPosts,
          handleLoadMore,
          filters,
          allKleinerTiptopfRecipesLoaded,
          produkttyp,
          totalPostsTiptopf,
          canLoadMore,
        }}
      >
        {children(items, sortType, loading)}
      </GridContext.Provider>
    </div>
  );
};

export default Grid;
